import { mapGetters, mapActions, mapMutations } from 'vuex'

import parseData from '@/utils/parseData'
import tooltips from '@/utils/tooltips'
import { statuses } from '@/utils/clusterVariables'
import ChatGPT from '@/components/ChatGPT/ChatGPT.vue'

export default {
  name: 'Sprints',
  data () {
    return {
      isLoading: {
        changeArticleTags: false,
        removeFromSprint: false,
        confirmSprint: false,
        markSprintCompleted: false,
        refresh: false,
        autocompleteArticles: false,
        createSprint: false,
        deleteSprint: false,
        addTask: false
      },

      settingsDialog: false,
      selectedMonth: null,
      activeTaskMonth: null,

      sprintsData: [],

      showConfirmSprintDialog: false,
      showDescriptionDialog: false,
      showCreateSprintDialog: false,
      showDeleteSprintDialog: false,
      showAddTaskDialog: false,
      showCopilot: false,
      selectedSprint: null,
      sprintToDelete: null,
      newSprintName: '',
      newSprintDescription: '',
      selectedArticleId: null,
      searchArticle: null,
      selectableArticles: [],
      sprintTaskType: 'new',
      sprintTaskTypeOptions: [
        { text: 'New article', value: 'new' },
        { text: 'Refresh article', value: 'update' }
      ],
      shortlistArticles: [],
      shortListTableHeaders: [
        { text: 'Article', value: 'title' },
        { text: 'Current status', value: 'status' },
        { text: 'Action', value: 'action' }
      ],

      statuses,
      autocompleteTimeout: null
    }
  },
  computed: {
    ...mapGetters([
      'topics',
      'activeContentplan',
      'activeWorkspace'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan ? this.activeContentplan.name : ''
    },
    activeSprint () {
      if (!this.sprintsData || this.sprintsData.length === 0) return null

      // Get all sprints with status 'active' that are not completed
      const activeSprints = this.sprintsData.filter(s => s.status === 'active')

      if (activeSprints.length === 0) return null

      // If multiple active sprints, return the newest one (highest ID or latest created_at)
      if (activeSprints.length > 1) {
        return activeSprints.reduce((newest, current) => {
          const newestId = newest.id || 0
          const currentId = current.id || 0
          return currentId > newestId ? current : newest
        })
      }

      return activeSprints[0]
    },
    pastSprints () {
      if (!this.sprintsData || this.sprintsData.length === 0) return []

      const activeSprintId = this.activeSprint?.id

      // Include completed sprints and any active sprints that aren't THE active sprint
      return this.sprintsData.filter(s =>
        s.status === 'completed' || (s.status === 'active' && s.id !== activeSprintId)
      ).sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0
        if (aTime !== bTime) return bTime - aTime
        const aId = a.id || 0
        const bId = b.id || 0
        return bId - aId
      })
    },
    remainingCredits () {
      return this.activeWorkspace?.remaining_article_credits
    },
    autocompleteArticles () {
      const existingIds = new Set(this.activeSprint?.tasks?.map(task => task.article_id) || [])

      return (this.selectableArticles || [])
        .map(article => {
          const articleId = article.article_id
          const display = article.title || article.article_url || article.url || `Article ${articleId}`
          return { ...article, article_id: articleId, display }
        })
        .filter(article => article.article_id && !existingIds.has(article.article_id))
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    }
  },
  methods: {
    parseData,
    tooltips,
    ...mapActions([
      'sprintsGet',
      'sprintsPost',
      'sprintsPut',
      'topicGetList',
      'articleGet',
      'articlePut',
      'sendWebsocketMessage'
    ]),

    ...mapMutations([
      'addProgressTracker',
      'setSnackbar'
    ]),

    // Helper method to get formatted status text
    getStatusText (statusValue) {
      const status = this.statuses.find(s => s.value === statusValue)
      return status ? status.text : statusValue
    },

    // Helper method to check if article is a refresh article (task type is update)
    isRefreshArticle (task) {
      return task.type === 'update'
    },

    // Helper method to check if this is the active sprint
    isActiveSprint (sprint) {
      return sprint && sprint.status === 'active'
    },

    defaultSprintName () {
      return new Date().toLocaleString('default', { month: 'long', year: 'numeric' })
    },
    openCreateSprintDialog () {
      this.newSprintName = this.defaultSprintName()
      this.newSprintDescription = ''
      this.showCreateSprintDialog = true
    },
    openAddTaskDialog () {
      this.selectedArticleId = null
      this.searchArticle = null
      this.selectableArticles = []
      this.sprintTaskType = 'new'
      this.showAddTaskDialog = true
    },

    // RENDER DATA
    async loadData () {
      document.title = 'Sprints | ContentGecko'

      try {
        const sprints = await this.sprintsGet({
          contentplanId: this.contentplanId
        })

        this.sprintsData = sprints

        await this.topicGetList({
          contentplanId: this.contentplanId
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.setActiveSlideGroupItem()
      }
    },
    async setActiveSlideGroupItem () {
      this.activeTaskMonth = this.pastSprints.length - 1
    },
    async changeArticleTags (articleId, tag, newValue) {
      if (!articleId || !tag) return

      const articleIds = [articleId]

      let action
      if (tag === 'status') {
        action = 'updateStatus'
      } else if (tag === 'priority') {
        action = 'updatePriority'
      } else if (tag === 'funnel') {
        action = 'updateFunnel'
      }

      this.isLoading.changeArticleTags = articleId || true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action,
          articleIds,
          [tag]: newValue
        })

        // Update the value in sprintsData
        this.sprintsData = this.sprintsData.map(sprint => {
          sprint.tasks = sprint.tasks.map(task => {
            if (task.article_id === articleId) {
              task['article_' + tag] = newValue
            }
            return task
          })
          return sprint
        })

        this.isLoading.changeArticleTags = false
      } catch (e) {
        console.log(e)
      }
    },
    async removeFromSprint (sprintId, taskId) {
      if (!taskId || !sprintId) return

      this.isLoading.removeFromSprint = taskId

      try {
        await this.sprintsPut({
          contentplanId: this.contentplanId,
          sprintId,
          action: 'removeTask',
          taskId
        })

        // Remove task from local state
        this.sprintsData = this.sprintsData.map(sprint => {
          if (sprint.id === sprintId) {
            sprint.tasks = sprint.tasks.filter(task => task.task_id !== taskId)
          }
          return sprint
        })

        this.setSnackbar({ open: true, text: 'Task removed from sprint', type: 'success' })
      } catch (error) {
        console.log(error)
        this.setSnackbar({ open: true, text: 'Failed to remove task from sprint', type: 'error' })
      } finally {
        this.isLoading.removeFromSprint = false
      }
    },
    async handleConfirmSprint (sprint) {
      this.shortlistArticles = sprint.tasks
      this.selectedSprint = sprint

      // Mark tasks that exceed available credits
      const availableCredits = this.activeWorkspace?.remaining_article_credits || 0
      this.shortlistArticles = this.shortlistArticles.map((task, index) => ({
        ...task,
        exceedsCredits: index >= availableCredits
      }))

      this.showConfirmSprintDialog = true
    },
    async processConfirmSprint () {
      this.showConfirmSprintDialog = false
      this.isLoading.confirmSprint = true

      const availableCredits = this.activeWorkspace?.remaining_article_credits || 0

      if (availableCredits <= 0) {
        this.setSnackbar({ open: true, text: 'You have no article credits remaining to write articles. Please upgrade your plan or purchase more credits.', type: 'error' })
        this.isLoading.confirmSprint = false
        return
      }

      // Limit tasks to available credits
      const tasksToWrite = this.shortlistArticles
        .filter(task => !task.exceedsCredits)
        .slice(0, availableCredits)

      // Reset status of refresh tasks to 'no status' so they are rewritten from scratch
      const refreshTasks = tasksToWrite.filter(task => this.isRefreshArticle(task))
      if (refreshTasks.length > 0) {
        try {
          await this.articlePut({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            action: 'updateStatus',
            articleIds: refreshTasks.map(t => t.article_id),
            status: 'no status'
          })
        } catch (error) {
          console.log('Failed to reset refresh task statuses:', error)
        }
      }

      // Add progress tracker for each task
      tasksToWrite.forEach((task) => {
        this.addProgressTracker({
          contentplanId: this.contentplanId,
          clusterId: task.article_id,
          name: task.title,
          stepsObject: {
            Briefing: 'inProgress',
            Researching: 'pending',
            Drafting: 'pending',
            Editing: 'pending',
            Illustrating: 'pending'
          }
        })
      })

      try {
        await this.sendWebsocketMessage({
          action: 'runOrchestrator',
          message: {
            contentplanId: this.contentplanId,
            runWriting: true,
            selectBy: 'cluster',
            clusterIds: tasksToWrite.map(t => t.article_id),
            publishTo: this.activeContentplan.main_cms_integration || 'autoblog',
            userType: 'managed'
          }
        })

        this.setSnackbar({ open: true, text: `Started writing process for ${tasksToWrite.length} articles`, type: 'success' })
      } catch (error) {
        console.log(error)
        this.setSnackbar({ open: true, text: 'Failed to start writing process', type: 'error' })
      } finally {
        this.isLoading.confirmSprint = false
      }
    },
    totalTasks (sprint) {
      return sprint?.tasks?.length || 0
    },
    taskList (sprint) {
      if (!sprint?.tasks) return []

      // Sort tasks by topic name, putting tasks without topics at the end
      return [...sprint.tasks].sort((a, b) => {
        const topicA = a.topic_name || 'zzz_no_topic'
        const topicB = b.topic_name || 'zzz_no_topic'
        return topicA.localeCompare(topicB)
      })
    },
    openDescriptionDialog (sprint) {
      this.selectedSprint = sprint
      this.showDescriptionDialog = true
    },
    copyDescriptionToClipboard () {
      const description = this.selectedSprint?.description || ''
      navigator.clipboard.writeText(description)
      this.setSnackbar({ open: true, text: 'Description copied to clipboard', type: 'success' })
    },
    toggleCopilot () {
      this.showCopilot = !this.showCopilot
    },
    async markSprintCompleted () {
      if (!this.activeSprint || !this.activeSprint.id) return

      this.isLoading.markSprintCompleted = true

      try {
        await this.sprintsPut({
          contentplanId: this.contentplanId,
          sprintId: this.activeSprint.id,
          action: 'updateStatus',
          status: 'completed'
        })

        this.setSnackbar({ open: true, text: 'Sprint marked as completed', type: 'success' })
        await this.loadData()
      } catch (error) {
        console.log(error)
        this.setSnackbar({ open: true, text: 'Failed to mark sprint as completed', type: 'error' })
      } finally {
        this.isLoading.markSprintCompleted = false
      }
    },
    async refreshSprints () {
      this.isLoading.refresh = true
      try {
        await this.loadData()
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading.refresh = false
      }
    },
    async loadAutocompleteArticles () {
      clearTimeout(this.autocompleteTimeout)
      this.autocompleteTimeout = setTimeout(async () => {
        if (!this.searchArticle) {
          this.selectableArticles = []
          return
        }

        this.isLoading.autocompleteArticles = true

        try {
          const filters = [{ field: 'title', op: 'regexp', value: this.searchArticle }]
          const response = await this.articleGet({
            contentplanId: this.contentplanId,
            filters,
            pagination: { page: 1, limit: 20 },
            straightReturn: true
          })
          this.selectableArticles = response?.items || response || []
        } catch (error) {
          console.log(error)
        } finally {
          this.isLoading.autocompleteArticles = false
        }
      }, 400)
    },
    async createSprint () {
      const sprintName = this.newSprintName?.trim()
      const sprintDescription = this.newSprintDescription?.trim()

      if (!sprintName) {
        this.setSnackbar({ open: true, text: 'Sprint name is required', type: 'error' })
        return
      }

      this.isLoading.createSprint = true

      try {
        await this.sprintsPost({
          contentplanId: this.contentplanId,
          name: sprintName,
          description: sprintDescription || undefined,
          status: 'active'
        })

        this.setSnackbar({ open: true, text: 'Sprint created', type: 'success' })
        this.showCreateSprintDialog = false
        await this.loadData()
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading.createSprint = false
      }
    },
    confirmDeleteSprint (sprint) {
      this.sprintToDelete = sprint
      this.showDeleteSprintDialog = true
    },
    async deleteSprint () {
      if (!this.sprintToDelete?.id) return

      this.isLoading.deleteSprint = true

      try {
        await this.sprintsPut({
          contentplanId: this.contentplanId,
          sprintId: this.sprintToDelete.id,
          action: 'deleteSprint'
        })

        this.setSnackbar({ open: true, text: 'Sprint deleted', type: 'success' })
        this.showDeleteSprintDialog = false
        this.sprintToDelete = null
        await this.loadData()
      } catch (error) {
        console.log(error)
        this.setSnackbar({ open: true, text: 'Failed to delete sprint', type: 'error' })
      } finally {
        this.isLoading.deleteSprint = false
      }
    },
    async addSelectedArticleToSprint () {
      if (!this.activeSprint?.id) {
        this.setSnackbar({ open: true, text: 'No active sprint selected', type: 'error' })
        return
      }

      const articleId = parseInt(this.selectedArticleId)

      if (!articleId) {
        this.setSnackbar({ open: true, text: 'Select an article first', type: 'error' })
        return
      }

      this.isLoading.addTask = true

      try {
        await this.sprintsPut({
          contentplanId: this.contentplanId,
          sprintId: this.activeSprint.id,
          action: 'addTask',
          articleId,
          type: this.sprintTaskType
        })

        this.setSnackbar({ open: true, text: 'Article added to sprint', type: 'success' })
        this.selectedArticleId = null
        this.searchArticle = null
        this.selectableArticles = []
        this.showAddTaskDialog = false
        await this.loadData()
      } catch (error) {
        console.log(error)
        this.setSnackbar({ open: true, text: 'Failed to add article to sprint', type: 'error' })
      } finally {
        this.isLoading.addTask = false
      }
    }
  },
  async mounted () {
    await this.loadData()
  },
  components: {
    ChatGPT
  }
}
